<template>
  <!--
    Every dismissal route is closed while submitting, not just the Cancel button.
    The parent captures its batch list before the first await, so a dismissal
    mid-run clears the modal state while the upload carries on to completion --
    the admin would believe they had stopped a bulk account removal that is still
    running.
  -->
  <b-modal
    id="confirm-csv-removal-modal"
    ref="confirm-csv-removal-modal"
    hide-footer
    title="Confirm removal requests"
    size="lg"
    :no-close-on-esc="isSubmitting"
    :no-close-on-backdrop="isSubmitting"
    :hide-header-close="isSubmitting"
    @hide="onHide($event)"
  >
    <div class="confirm-csv-removal__wrapper">
      <p>
        <strong>{{ emails.length }}</strong> email{{ emails.length === 1 ? '' : 's' }} will be queued for account removal.
        <span v-if="droppedCount > 0">{{ droppedCount }} entr{{ droppedCount === 1 ? 'y was' : 'ies were' }} dropped (blank, duplicate, or not a valid email).</span>
      </p>
      <div class="confirm-csv-removal__list">
        <ul class="pl-3 mb-0">
          <li v-for="email in emails" :key="email">{{ email }}</li>
        </ul>
      </div>
      <div class="form-group mt-3 mb-0">
        <label for="csv-removal-confirm-text">Type <strong>confirm</strong> to continue</label>
        <input
          id="csv-removal-confirm-text"
          v-model="confirmText"
          type="text"
          class="form-control"
          autocomplete="off"
          placeholder="confirm"
          :disabled="isSubmitting"
          @keyup.enter="canConfirm && confirm()"
        >
      </div>
      <div class="d-flex justify-content-between align-items-center mt-3">
        <span class="text-muted small">{{ progress }}</span>
        <div>
          <button class="mr-2 btn btn-secondary" :disabled="isSubmitting" @click="closeModal()">
            Cancel
          </button>
          <button class="btn btn-danger" :disabled="!canConfirm" @click="confirm()">
            <i v-if="isSubmitting" class="fa fa-spinner fa-spin mr-1"></i>
            Confirm removal
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'ConfirmCsvRemovalModal',
  props: {
    emails: {
      type: Array,
      required: true,
    },
    droppedCount: {
      type: Number,
      default: 0,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    // "Batch 3 of 12" while the upload runs, empty otherwise. The run is long and
    // unstoppable, so a bare spinner would leave the admin unable to tell a stalled
    // upload from a slow one.
    progress: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      confirmText: '',
    }
  },
  computed: {
    canConfirm() {
      return !this.isSubmitting
        && this.emails.length > 0
        && this.confirmText.trim().toLowerCase() === 'confirm'
    },
  },
  mounted() {
    this.$refs['confirm-csv-removal-modal'].show()
  },
  methods: {
    // The disabled Cancel button and the three no-close props cover every route a
    // user can take, but `hide` also fires for programmatic and future ones. Veto
    // there too so the guard cannot be bypassed by a route nobody listed.
    onHide(event) {
      if (this.isSubmitting) {
        event.preventDefault()
        return
      }
      this.$emit('closeConfirmCsvRemovalModal')
    },
    closeModal() {
      this.$refs['confirm-csv-removal-modal'].hide()
    },
    confirm() {
      if (!this.canConfirm) return
      this.$emit('confirmCsvRemoval')
    },
  },
}
</script>

<style lang="scss" scoped>
.confirm-csv-removal__wrapper {
  font-family: "Nunito", sans-serif !important;
}
.confirm-csv-removal__list {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
}
</style>
